import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { initKvIntro } from '../animation/kv-intro';
import { getDelayForViewportEntry } from '../animation/scroll-animations';

gsap.registerPlugin(ScrollTrigger, SplitText);

initKvIntro({
  titleSelector: '.js-members-title',
  subtitleSelector: '.js-members-subtitle',
  leadSelector: '.js-members-lead',
});

window.addEventListener('load', () => {
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const isSP = window.matchMedia('(max-width: 1024px)').matches;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (isSP) {
          gsap.utils.toArray('.p-aboutus-members__card').forEach(el => {
            gsap.set(el, { clearProps: 'opacity,transform' });
          });
          const grid = document.querySelector('.p-aboutus-members__grid');
          if (grid) {
            gsap.set(grid, { opacity: 0 });
            gsap.to(grid, {
              opacity: 1,
              ease: 'power3.out',
              duration: 0.8,
              scrollTrigger: {
                trigger: grid,
                start: 'top 85%',
                once: true,
              },
            });
          }
        } else {
          gsap.utils.toArray('.p-aboutus-members__card').forEach(el => {
            gsap.set(el, { y: 40, opacity: 0 });
            gsap.to(el, {
              y: 0,
              opacity: 1,
              ease: 'power3.out',
              duration: 0.8,
              delay: getDelayForViewportEntry(el),
              scrollTrigger: {
                trigger: el,
                start: 'top 85%',
                once: true,
              },
            });
          });
        }

        ScrollTrigger.refresh();
      });
    });
  } else {
    ScrollTrigger.refresh();
  }
});

/**
 * メンバーカードのクリックでポップアップを開閉する。
 *
 * - 閉じ方: 背景クリック / ESC / 閉じるボタン
 * - 動画 (data-src) は開いた時に lazy load し autoplay (muted/loop)
 * - フォーカス管理: 開く時は閉じるボタン、閉じる時は元のカードへ戻す
 * - Tab フォーカストラップ + body スクロールロック
 */
(function initMemberModal() {
  const root = document.querySelector('.js-member-modal-root');
  if (!root) return;
  const triggers = document.querySelectorAll('.js-member-card-trigger');
  if (!triggers.length) return;

  const FOCUSABLE = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let activeModal = null;
  let lastFocusedTrigger = null;

  /**
   * モーダル内のメディア (画像 / 動画) を data-src から実 src に昇格させる。
   * 開いた時に初めてネットワーク取得を発火させ、初回ページロードのコストを抑える。
   */
  function activateMedia(modal) {
    // 画像
    const img = modal.querySelector('img[data-src]');
    if (img && !img.getAttribute('src')) {
      img.setAttribute('src', img.dataset.src);
    }
    // 動画
    const video = modal.querySelector('video');
    if (!video) return;
    const source = video.querySelector('source[data-src]');
    if (source && !source.getAttribute('src')) {
      source.setAttribute('src', source.dataset.src);
      try {
        video.load();
      } catch (e) {
        /* noop */
      }
    }
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        /* autoplay 失敗時は無視 (muted なら通常許可される) */
      });
    }
  }

  function deactivateVideo(modal) {
    const video = modal.querySelector('video');
    if (!video) return;
    try {
      video.pause();
      video.currentTime = 0;
    } catch (e) {
      /* noop */
    }
  }

  function openModal(modalId, triggerEl) {
    const modal = document.getElementById(modalId);
    if (!modal) return;
    if (activeModal && activeModal !== modal) {
      closeModal(false);
    }
    activeModal = modal;
    lastFocusedTrigger = triggerEl || null;

    // スクロールバー幅を計測してパディング補填用 CSS 変数にセット
    // (body の overflow:hidden で消えるスクロールバー分の横ジャンプを防止)
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty(
      '--member-modal-scrollbar-width',
      scrollbarWidth + 'px'
    );

    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-member-modal-open');

    activateMedia(modal);

    const closeBtn = modal.querySelector('[data-close-modal]');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  // 閉じアニメ完了待ちのフォールバックタイマー (transitionend 未発火対策)
  let closeFallbackTimer = null;

  function closeModal(returnFocus) {
    if (!activeModal) return;
    const modal = activeModal;
    const triggerToRestore = lastFocusedTrigger;

    // 先に内部状態をリセット (連続クリックや即時 openModal に備える)
    activeModal = null;
    lastFocusedTrigger = null;
    deactivateVideo(modal);

    // 後始末。is-closing でアウトアニメを再生し、完了後に is-open を外して
    // display:none へ戻す (フォーカス復帰・スクロールロック解除もここで)。
    const finalize = () => {
      if (closeFallbackTimer) {
        clearTimeout(closeFallbackTimer);
        closeFallbackTimer = null;
      }
      modal.removeEventListener('transitionend', onEnd);
      modal.classList.remove('is-open', 'is-closing');
      modal.setAttribute('aria-hidden', 'true');
      root.classList.remove('is-open', 'is-closing');
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-member-modal-open');
      document.body.style.removeProperty('--member-modal-scrollbar-width');
      if (returnFocus !== false && triggerToRestore) {
        triggerToRestore.focus({ preventScroll: true });
      }
    };
    const onEnd = e => {
      // modal 自身の transition 完了でのみ確定 (子要素の transition は無視)
      if (e.target === modal) finalize();
    };

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      finalize();
      return;
    }

    root.classList.add('is-closing');
    modal.classList.add('is-closing');
    modal.addEventListener('transitionend', onEnd);
    // transitionend が発火しない環境向けフォールバック (CSS 320ms + 余裕)
    closeFallbackTimer = setTimeout(finalize, 400);
  }

  function trapFocus(e) {
    if (!activeModal || e.key !== 'Tab') return;
    const focusables = activeModal.querySelectorAll(FOCUSABLE);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const id = trigger.getAttribute('aria-controls');
      if (id) openModal(id, trigger);
    });
  });

  root.addEventListener('click', e => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-close-modal]')) {
      closeModal();
    }
  });

  document.addEventListener('keydown', e => {
    if (!activeModal) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
      return;
    }
    trapFocus(e);
  });
})();
